import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getMe, getAssignedFindings, finalInspectionLoopActions, loopPhotoUrl,
  SEVERITY_LABEL, LOOP_STATUS, type LoopFinding, type MeResponse,
} from '../lib/api';
import CreateTaskHere from '../components/CreateTaskHere';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';

/** PM portal feed (Feature A) — the findings assigned to the logged-in site_pm: acknowledge a remediation
 *  date, then submit a resolution WITH a photo (the engine refuses a photo-less resolution). */
export default function MyFindings() {
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: getMe });
  const { data: findings, isLoading } = useQuery<LoopFinding[]>({
    queryKey: ['assigned-findings'], queryFn: getAssignedFindings, enabled: me?.account?.role === 'site_pm',
  });

  if (!me?.authenticated) return <div className="card">请先登录 / please log in.</div>;
  if (me.account?.role !== 'site_pm') return <div className="card">此页仅项目经理可见 / site-PM only.</div>;

  return (
    <div>
      <h2>我的整改任务 / My remediation findings</h2>
      {isLoading && <div className="card"><Skeleton lines={3} /></div>}
      {findings && findings.length === 0 && <EmptyState title="暂无待办 / Nothing assigned" hint="GM 指派的整改项会出现在这里。 / Findings assigned by the GM land here." />}
      {(findings ?? []).map((f) => <FindingCard key={f.id} f={f} />)}
    </div>
  );
}

function FindingCard({ f }: { f: LoopFinding }) {
  const qc = useQueryClient();
  const [date, setDate] = useState(f.promised_remediation_date ?? '');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function ack() {
    if (!date) { setErr('请填写整改完成日期 / pick a date'); return; }
    setBusy(true); setErr(null);
    try { await finalInspectionLoopActions.acknowledge(f.id, date); await qc.invalidateQueries({ queryKey: ['assigned-findings'] }); }
    catch (e: any) { setErr(e?.body?.detail ?? e?.body?.error ?? e?.message ?? '失败'); }
    finally { setBusy(false); }
  }
  async function resolve() {
    if (!file) { setErr('整改完成必须上传照片 / a photo is required'); return; }
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append('notes_md', notes);
      fd.append('photo', file);
      await finalInspectionLoopActions.resolve(f.id, fd);
      await qc.invalidateQueries({ queryKey: ['assigned-findings'] });
    } catch (e: any) { setErr(e?.body?.detail ?? e?.body?.error ?? e?.message ?? '失败'); }
    finally { setBusy(false); }
  }

  const sev = SEVERITY_LABEL[f.severity];
  const st = LOOP_STATUS[f.status];
  const canAck = f.status === 'assigned' || f.status === 'pm_acknowledged';
  const canResolve = f.status === 'in_remediation' || f.status === 'pm_acknowledged' || f.status === 'assigned';

  return (
    <div className="card" style={{ marginBottom: 10 }}>
      <div className="row-wrap" style={{ gap: 8, alignItems: 'center', marginBottom: 6 }}>
        <strong>{f.label_md}</strong>
        {sev && <span className={`pill ${sev.pill} small`}>{sev.label}</span>}
        {st && <span className={`pill ${st.pill} small`}>{st.label}</span>}
        {f.gm_missed_by_pm ? <span className="pill pill-bad small">PM 漏检</span> : null}
      </div>
      <div className="row-wrap small" style={{ gap: 12, marginBottom: 8, alignItems: 'center' }}>
        <a href={loopPhotoUrl(f.id, 'finding')} target="_blank" rel="noreferrer">GM 问题照片 / finding photo ↗</a>
        {f.promised_remediation_date && <span className="muted">承诺完成 / promised: {f.promised_remediation_date}</span>}
        <CreateTaskHere
          context={{ kind: 'finding', label: f.label_md, finding_id: f.id }}
          pageUrl="/my-findings"
          prefillTitle={`整改跟进：${f.label_md}`}
          compact
        />
      </div>

      {err && <div className="small" style={{ color: 'var(--bad)', marginBottom: 6 }}>{err}</div>}

      {canAck && (
        <div className="row-wrap" style={{ alignItems: 'flex-end', gap: 8, marginBottom: 8 }}>
          <div className="field" style={{ marginBottom: 0 }}><label>整改完成日期 / remediation date</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <button disabled={busy} onClick={ack}>确认并承诺日期 / Acknowledge</button>
        </div>
      )}

      {canResolve && f.status !== 'pm_resolved' && (
        <div className="row-wrap" style={{ alignItems: 'flex-end', gap: 8 }}>
          <div className="field" style={{ flex: 1, minWidth: 180, marginBottom: 0 }}><label>整改说明 / notes</label><input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="已完成的整改" /></div>
          <div className="field" style={{ marginBottom: 0 }}><label>整改照片（必填）/ photo (required)</label><input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></div>
          <button className="btn-primary" disabled={busy} onClick={resolve}>提交整改 / Submit resolution</button>
        </div>
      )}

      {f.status === 'pm_resolved' && <div className="muted small">已提交，等待 GM 复核 / submitted — awaiting GM verification.</div>}
    </div>
  );
}
